import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase, TechnicalCooperationRow } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const DEFAULT_CATEGORY = "T\u1ea5t c\u1ea3";

const TechCooperationPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const navigate = useNavigate();

  const [items, setItems] = useState<TechnicalCooperationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(DEFAULT_CATEGORY);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<string[]>([DEFAULT_CATEGORY]);
  const [sort, setSort] = useState<"newest" | "oldest" | "title-asc" | "title-desc">("newest");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const PER_PAGE = 9;

  const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/tech-cooperation-api`;

  const sortLabels: Record<string, string> = {
    newest: isKr ? "\ucd5c\uc2e0 \uc21c" : "M\u1edbi nh\u1ea5t",
    oldest: isKr ? "\uc624\ub798\ub41c \uc21c" : "C\u0169 nh\u1ea5t",
    "title-asc": isKr ? "\uc81c\ubaa9 A-Z" : "Ti\u00eau \u0111\u1ec1 A-Z",
    "title-desc": isKr ? "\uc81c\ubaa9 Z-A" : "Ti\u00eau \u0111\u1ec1 Z-A",
  };

  const clearFilters = () => {
    setSearch("");
    setActiveCategory(DEFAULT_CATEGORY);
    setPage(1);
    setSort("newest");
  };

  const hasFilters = search.trim() || activeCategory !== DEFAULT_CATEGORY || sort !== "newest";

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${EDGE_URL}?limit=200`, { method: "GET" });
        const json = await res.json();
        if (res.ok && json.data) {
          const unique = Array.from(
            new Set(
              (json.data as Record<string, unknown>[])
                .map((r) => r.category as string)
                .filter(Boolean)
            )
          );
          setCategories([DEFAULT_CATEGORY, ...unique]);
        }
      } catch {
        // fallback: keep default
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(PER_PAGE));
        params.set("sort", sort);
        if (activeCategory !== DEFAULT_CATEGORY) {
          params.set("category", activeCategory);
        }
        if (search.trim()) {
          params.set("search", search.trim());
        }

        const res = await window.fetch(`${EDGE_URL}?${params.toString()}`, { method: "GET" });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || "Fetch failed");

        const rows: TechnicalCooperationRow[] = (json.data ?? []).map((r: Record<string, unknown>) => ({
          id: r.id as string,
          title: r.title as string,
          category: (r.category as string) || undefined,
          content: (r.content as string) || undefined,
          image_url: (r.image_url as string) || undefined,
          created_at: (r.created_at as string) || undefined,
          updated_at: (r.updated_at as string) || undefined,
        }));
        setItems(rows);
        setTotal(json.count ?? 0);
      } catch (err) {
        console.error("Fetch error:", err);
        setItems([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [page, activeCategory, search, sort]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative py-16 overflow-hidden" style={{ minHeight: 220 }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#001428] via-[#004A99] to-[#002d5f]" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#00AEEF] -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-[#004A99] translate-y-1/2 -translate-x-1/4" />
        </div>

        <div className="max-w-[1200px] mx-auto px-6 relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-[2px] bg-[#00AEEF]" />
            <span className="text-[#00AEEF] text-xs font-bold uppercase tracking-widest">
              {isKr ? "한국-베트남" : "H\u00e0n Qu\u1ed1c – Vi\u1ec7t Nam"}
            </span>
          </div>
          <h1 className="text-white font-black text-4xl mb-3" style={{ letterSpacing: "-0.02em" }}>
            {isKr ? "\uae30\uc220 \ud611\ub825" : "H\u1ee3p T\u00e1c K\u1ef9 Thu\u1eadt"}
          </h1>
          <p className="text-white/70 text-base max-w-lg">
            {isKr
              ? "\ucd5c\uc2e0 \ud55c\uad6d \uae30\uc220 \ud611\ub825 \uc815\ubcf4\uc640 \ud504\ub85c\uc81d\ud2b8"
              : "Th\u00f4ng tin h\u1ee3p t\u00e1c k\u1ef9 thu\u1eadt H\u00e0n Qu\u1ed1c – Vi\u1ec7t Nam m\u1edbi nh\u1ea5t"}
          </p>

          <div className="mt-6 max-w-md">
            <div className="flex items-center bg-white rounded-xl overflow-hidden">
              <i className="ri-search-line text-gray-400 text-sm ml-4" />
              <input
                type="text"
                placeholder={isKr ? "\uc81c\ubaa9 \uac80\uc0c9..." : "T\u00ecm ki\u1ebfm theo ti\u00eau \u0111\u1ec1..."}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="flex-1 px-3 py-3 text-sm text-gray-700 outline-none bg-transparent"
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  className="px-3 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white border-b border-gray-100 sticky top-[103px] z-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex gap-0 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setPage(1);
                }}
                className={`flex-shrink-0 px-5 py-3.5 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap border-b-2 ${
                  activeCategory === cat
                    ? "text-[#004A99] border-[#004A99]"
                    : "text-gray-500 border-transparent hover:text-[#004A99]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {activeCategory === DEFAULT_CATEGORY
                ? isKr
                  ? "\uc804\uccb4 \uae30\uc0ac"
                  : "T\u1ea5t c\u1ea3 b\u00e0i vi\u1ebft"
                : activeCategory}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isKr ? `${total}\uac1c \uae30\uc0ac` : `${total} b\u00e0i vi\u1ebft`}
              {search && ` ${isKr ? "\uac80\uc0c9 \uacb0\uacfc" : `k\u1ebft qu\u1ea3 cho "${search}"`}`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Sort dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:border-[#004A99] hover:text-[#004A99] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-up-down-line" />
                {sortLabels[sort]}
                <i className="ri-arrow-down-s-line" />
              </button>
              {showSortDropdown && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg z-30 min-w-[160px]">
                  {Object.entries(sortLabels).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSort(key as typeof sort);
                        setShowSortDropdown(false);
                        setPage(1);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[#EDF2FB] cursor-pointer whitespace-nowrap transition-colors ${
                        sort === key ? "text-[#004A99] font-semibold" : "text-gray-600"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear filters */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line" />
                {isKr ? "\ud544\ud130 \ucd08\uae30\ud654" : "X\u00f3a l\u1ecdc"}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="h-44 bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                  <div className="h-5 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24">
            <i className="ri-file-list-3-line text-5xl text-gray-300 block mb-4" />
            <p className="text-gray-500 font-medium">
              {isKr
                ? "\uac8c\uc2dc\ub41c \uae30\uc0ac\uac00 \uc5c6\uc2b5\ub2c8\ub2e4"
                : "Ch\u01b0a c\u00f3 b\u00e0i vi\u1ebft n\u00e0o"}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {isKr
                ? "\uad00\ub9ac\uc790\uac00 \uae30\uc0ac\ub97c \uac8c\uc2dc\ud558\uba74 \uc5ec\uae30\uc5d0 \ud45c\uc2dc\ub429\ub2c8\ub2e4"
                : "B\u00e0i vi\u1ebft s\u1ebd hi\u1ec3n th\u1ecb khi admin \u0111\u0103ng b\u00e0i"}
            </p>
          </div>
        ) : (
          <>
            {page === 1 && items[0] && (
              <div
                onClick={() => navigate(`/hop-tac-ky-thuat/${items[0].id}`)}
                className="group bg-white rounded-2xl overflow-hidden mb-8 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col lg:flex-row">
                  {items[0].image_url ? (
                    <div className="lg:w-1/2 h-60 lg:h-auto overflow-hidden flex-shrink-0">
                      <img
                        src={items[0].image_url}
                        alt={items[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="lg:w-1/2 h-60 lg:h-auto bg-gradient-to-br from-[#004A99] to-[#00AEEF] flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-list-3-line text-white/40 text-6xl" />
                    </div>
                  )}
                  <div className="p-8 flex flex-col justify-center lg:w-1/2">
                    {items[0].category && (
                      <div className="mb-3">
                        <span className="text-[10px] font-bold text-[#004A99] bg-[#004A99]/10 px-2.5 py-1 rounded-full">
                          {items[0].category}
                        </span>
                      </div>
                    )}
                    <h2 className="text-xl font-black text-gray-800 leading-tight group-hover:text-[#004A99] transition-colors mb-4">
                      {items[0].title}
                    </h2>
                    <div className="flex items-center gap-4 mt-auto">
                      <span className="text-xs text-gray-400">
                        {formatDate(items[0].created_at)}
                      </span>
                      <span className="ml-auto text-xs text-[#004A99] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        {isKr ? "\uc77d\uae30" : "\u0110\u1ecdc b\u00e0i"}
                        <i className="ri-arrow-right-line" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(page === 1 ? items.slice(1) : items).map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate(`/hop-tac-ky-thuat/${item.id}`)}
                  className="group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-md transition-all duration-300 flex flex-col"
                >
                  <div className="h-44 overflow-hidden flex-shrink-0">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#004A99]/10 to-[#00AEEF]/10 flex items-center justify-center">
                        <i className="ri-file-list-3-line text-[#004A99]/30 text-4xl" />
                      </div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    {item.category && (
                      <span className="text-[10px] font-bold text-[#004A99] bg-[#004A99]/10 px-2 py-0.5 rounded-full inline-block mb-2 whitespace-nowrap w-fit">
                        {item.category}
                      </span>
                    )}
                    <h3 className="text-sm font-bold text-gray-800 leading-snug group-hover:text-[#004A99] transition-colors flex-1 line-clamp-3 mb-3">
                      {item.title}
                    </h3>
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
                      <span className="text-xs text-gray-400">
                        {formatDate(item.created_at)}
                      </span>
                      <span className="text-xs text-[#004A99] font-semibold flex items-center gap-0.5 group-hover:gap-1.5 transition-all whitespace-nowrap">
                        {isKr ? "\uc77d\uae30" : "\u0110\u1ecdc"}
                        <i className="ri-arrow-right-line text-[10px]" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:border-[#004A99] hover:text-[#004A99] disabled:opacity-40 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition-colors cursor-pointer ${
                      page === i + 1
                        ? "bg-[#004A99] text-white"
                        : "border border-gray-200 text-gray-600 hover:border-[#004A99] hover:text-[#004A99]"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:border-[#004A99] hover:text-[#004A99] disabled:opacity-40 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default TechCooperationPage;